"use client";

import { useState, useRef, useEffect } from "react";

interface Sample {
  id: string;
  language: string;
  reference: string;
  hypothesis: string;
  detected_language?: string;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface SampleTableProps {
  samples: Sample[];
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onSearch: (query: string) => void;
  searchQuery: string;
  loading: boolean;
}

function highlightMismatches(ref: string, hyp: string): { refParts: { text: string; match: boolean }[]; hypParts: { text: string; match: boolean }[] } {
  const refWords = ref.split(/\s+/);
  const hypWords = hyp.split(/\s+/);
  const maxLen = Math.max(refWords.length, hypWords.length);

  const refParts: { text: string; match: boolean }[] = [];
  const hypParts: { text: string; match: boolean }[] = [];

  for (let i = 0; i < maxLen; i++) {
    const rw = refWords[i] || "";
    const hw = hypWords[i] || "";
    const match = rw === hw;
    if (rw) refParts.push({ text: rw, match });
    if (hw) hypParts.push({ text: hw, match });
  }

  return { refParts, hypParts };
}

function highlightSearch(text: string, query: string): React.ReactNode {
  if (!query) return text;
  let re: RegExp;
  try {
    re = new RegExp(`(${query})`, "gi");
  } catch {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    re = new RegExp(`(${escaped})`, "gi");
  }
  const parts = text.split(re);
  return parts.map((part, i) =>
    re.test(part) ? (
      <mark key={i} className="bg-[#6366f1]/30 text-[var(--color-foreground)] rounded-sm px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

export default function SampleTable({ samples, pagination, onPageChange, onSearch, searchQuery, loading }: SampleTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [inputVal, setInputVal] = useState(searchQuery);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external searchQuery changes
  useEffect(() => {
    setInputVal(searchQuery);
  }, [searchQuery]);

  const handleInput = (val: string) => {
    setInputVal(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearch(val);
    }, 400);
  };

  const handleClear = () => {
    setInputVal("");
    onSearch("");
    inputRef.current?.focus();
  };

  // Regex validity indicator
  let regexValid = true;
  if (inputVal) {
    try {
      new RegExp(inputVal, "i");
    } catch {
      regexValid = false;
    }
  }

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg overflow-hidden">
      {/* Header + Search */}
      <div className="px-5 py-3 border-b border-[var(--color-border)] flex justify-between items-center gap-4">
        <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide whitespace-nowrap">
          Sample Predictions
        </h3>

        {/* Search field */}
        <div className="relative flex-1 max-w-md">
          <input
            ref={inputRef}
            type="text"
            value={inputVal}
            onChange={(e) => handleInput(e.target.value)}
            placeholder="Search ID, text, or /regex/..."
            className={`w-full bg-[var(--color-background)] border rounded px-3 py-1.5 text-[11px] text-[var(--color-foreground)] placeholder:text-[var(--color-muted)] focus:outline-none focus:border-[var(--color-border-light)] transition-colors ${
              !regexValid ? "border-[var(--color-danger)]" : "border-[var(--color-border)]"
            }`}
          />
          {inputVal && (
            <button
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-muted)] hover:text-[var(--color-foreground)] text-[10px]"
            >
              esc
            </button>
          )}
          {!regexValid && (
            <span className="absolute right-8 top-1/2 -translate-y-1/2 text-[9px] text-[var(--color-danger)]">
              bad regex
            </span>
          )}
        </div>

        <span className="text-[10px] text-[var(--color-muted)] whitespace-nowrap">
          {pagination.total} samples &middot; page {pagination.page}/{pagination.totalPages}
        </span>
      </div>

      {loading ? (
        <div className="p-8 text-center text-[var(--color-muted)] text-xs">Loading...</div>
      ) : samples.length === 0 ? (
        <div className="p-8 text-center text-[var(--color-muted)] text-xs">
          {searchQuery ? "No samples match your search." : "No samples available."}
        </div>
      ) : (
        <>
          {/* Stacked sample cards */}
          <div className="divide-y divide-[var(--color-border)]">
            {samples.map((s) => {
              const isExpanded = expandedId === s.id;
              const { refParts, hypParts } = highlightMismatches(s.reference, s.hypothesis);
              const isMatch = s.reference === s.hypothesis;

              return (
                <div
                  key={s.id}
                  className="px-5 py-3 hover:bg-[var(--color-card-hover)] cursor-pointer transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : s.id)}
                >
                  {/* Sample header row */}
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[10px] font-mono text-[var(--color-muted)]">
                      {searchQuery ? highlightSearch(s.id, searchQuery) : s.id}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-card-hover)] text-[var(--color-accent)] capitalize">
                      {s.language}
                    </span>
                    {isMatch && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-success)]/10 text-[var(--color-success)]">
                        exact match
                      </span>
                    )}
                  </div>

                  {/* Reference - stacked on top */}
                  <div className="mb-1.5">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--color-muted)] mr-2">ref</span>
                    <span className="text-[11px] text-[var(--color-foreground)] leading-relaxed">
                      {isExpanded
                        ? refParts.map((p, i) => (
                            <span
                              key={i}
                              className={p.match ? "" : "bg-[#ef4444]/15 text-[#f87171] rounded-sm px-0.5"}
                            >
                              {p.text}{" "}
                            </span>
                          ))
                        : searchQuery
                          ? highlightSearch(s.reference, searchQuery)
                          : s.reference}
                    </span>
                  </div>

                  {/* Hypothesis - stacked below */}
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-[var(--color-muted)] mr-2">hyp</span>
                    <span className="text-[11px] text-[var(--color-foreground)] leading-relaxed">
                      {isExpanded
                        ? hypParts.map((p, i) => (
                            <span
                              key={i}
                              className={p.match ? "" : "bg-[#eab308]/15 text-[#fbbf24] rounded-sm px-0.5"}
                            >
                              {p.text}{" "}
                            </span>
                          ))
                        : searchQuery
                          ? highlightSearch(s.hypothesis, searchQuery)
                          : s.hypothesis}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="px-5 py-3 border-t border-[var(--color-border)] flex justify-between items-center">
            <button
              onClick={(e) => { e.stopPropagation(); onPageChange(pagination.page - 1); }}
              disabled={pagination.page <= 1}
              className="text-[11px] px-3 py-1 rounded border border-[var(--color-border)] text-[var(--color-muted)] hover:text-[var(--color-foreground)] hover:border-[var(--color-border-light)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                let pageNum: number;
                if (pagination.totalPages <= 7) {
                  pageNum = i + 1;
                } else if (pagination.page <= 4) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 3) {
                  pageNum = pagination.totalPages - 6 + i;
                } else {
                  pageNum = pagination.page - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={(e) => { e.stopPropagation(); onPageChange(pageNum); }}
                    className={`text-[10px] w-6 h-6 rounded transition-colors ${
                      pageNum === pagination.page
                        ? "bg-[var(--color-card-hover)] text-[var(--color-foreground)]"
                        : "text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onPageChange(pagination.page + 1); }}
              disabled={pagination.page >= pagination.totalPages}
              className="text-[11px] px-3 py-1 rounded border border-[var(--color-border)] text-[var(--color-muted)] hover:text-[var(--color-foreground)] hover:border-[var(--color-border-light)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
